import { AlertType } from "@caspeco/casper-ui-library.components.alert";
import i18next from "i18next";
import { Menu } from "types/menu";
import { AddableToCartFailReasons, Article, ArticleAddon } from "types/article";
import { ICartRowDraft, CartRow, CartRowDraft } from "types/cart";
import { PriceType } from "types/price";
import { Section } from "types/section";

type PriceTypeKey = "takeAwayPriceIncludingVat" | "inHousePriceIncludingVat";
type SubArticleTypeKey = "options" | "extras";

const getPriceArray = (
	article: Article,
	selectedIds: string[],
	subArticleTypeKey: SubArticleTypeKey,
	priceTypeKey: PriceTypeKey,
): number[] =>
	selectedIds
		.flatMap((id) =>
			article[subArticleTypeKey].map(
				(subArticle) => subArticle.items.find((item) => item.id === id)?.[priceTypeKey].cents ?? 0,
			),
		)
		.filter((price) => price > 0);

const getSubTotal = (
	article: Article,
	selectedIds: string[],
	subArticleTypeKey: SubArticleTypeKey,
	priceTypeKey: PriceTypeKey,
): number => {
	const priceArray = getPriceArray(article, selectedIds, subArticleTypeKey, priceTypeKey);
	return priceArray.reduce((a, b) => a + b, 0);
};

const getFirstEnabledOptionId = (optionGroup: ArticleAddon): string | undefined => {
	const firstEnabledOption = optionGroup.items.find((item) => item.status === "ENABLED");
	return firstEnabledOption?.id;
};

/**
 * Used to get an initial selection of options for article,
 * as it is required to select one per option group.
 *
 * The initial and default selection should be the first enabled option item
 * of every option group.
 *
 * @returns list of option item ids
 */
export const getEveryFirstOptionPerOptionGroup = (article: Article): string[] => {
	// Per option group, return the id of the first item that is enabled, which is the default selection
	const everyFirstEnabledOptionId: string[] = article.options
		.map((optionGroup) => getFirstEnabledOptionId(optionGroup))
		.filter((id): id is string => id !== undefined);

	return everyFirstEnabledOptionId.length > 0 ? everyFirstEnabledOptionId : [];
};

/**
 * Calculate the total price of adding/updating article as given cartRow
 *
 * @param priceType type of price to use, based on selected delivery type
 * @param article article seen in modal
 * @param cartRow current cartRow settings, including article options, extras and article quantity
 */
export const getTotalPrice = (priceType: PriceType, article: Article, cartRow: ICartRowDraft): number => {
	const { quantity, extras, options } = cartRow;
	const priceTypeKey: PriceTypeKey =
		priceType === PriceType.TAKEAWAY ? "takeAwayPriceIncludingVat" : "inHousePriceIncludingVat";

	const extrasTotal = getSubTotal(article, extras, "extras", priceTypeKey) * quantity;

	const optionsTotal = getSubTotal(article, options, "options", priceTypeKey) * quantity;

	const articleTotal = article[priceTypeKey].cents * cartRow.quantity;
	return articleTotal + extrasTotal + optionsTotal;
};

/**
 * Creates a INewCartRow-object that holds user selection related to the article
 * displayed in ArticleModal.
 *
 * @param article Article object with all original properties.
 * @param defaultValues Default values with the user selection related to given article.
 * @returns an empty cart row with default quantity, extra- and option selections
 * or a cart row with pre defined selections and quantity (if selectedCartRow is provided).
 */
export const initializeCartRow = (
	article: Article,
	defaultValues: CartRow | CartRowDraft | undefined,
): CartRowDraft => {
	// Default values for empty new cart row
	let extras: string[] = [];
	let options: string[] = getEveryFirstOptionPerOptionGroup(article);
	let quantity: number = 1;

	if (defaultValues instanceof CartRow) {
		quantity = defaultValues.quantity;
		extras = defaultValues.article.extraGroups.flatMap((extraGroup) =>
			extraGroup.addons.map((addon) => addon.id),
		);
		options = defaultValues.article.optionGroups.flatMap((optionGroup) =>
			optionGroup.addons.map((addon) => addon.id),
		);
	} else if (defaultValues instanceof CartRowDraft) {
		quantity = defaultValues.quantity;
		extras = defaultValues.extras;
		options = defaultValues.options;
	}

	return new CartRowDraft({
		quantity,
		itemId: article.id,
		extras,
		options,
	});
};

const sectionIncludesArticle = (section: Section, articleId: string): boolean =>
	section.articles.some((article) => article.id === articleId);

export const getSectionForArticle = (menu: Menu, articleId: string): Section | undefined =>
	menu.sections.find((section) => sectionIncludesArticle(section, articleId));

/**
 * Replace any previosly selected option item in the same option group with the next value.
 *
 * Only one option from each option group can (and is required to) be selected.
 */
export const getNextOptionsSelection = (nextValue: string, article: Article, cartRow: ICartRowDraft) => {
	const result = cartRow.options;
	const includesValue = (optionGroup: ArticleAddon) => {
		const ids = optionGroup.items.map((item) => item.id);
		return ids.includes(nextValue);
	};

	const optionGroup = article.options.find(includesValue);
	const optionGroupItemIds = optionGroup?.items.map((item) => item.id);

	const currentSelection = result.find((option) => optionGroupItemIds?.includes(option));

	const indexOfOptionToRemove = result.indexOf(currentSelection as string);
	result.splice(indexOfOptionToRemove, 1, nextValue);
	return result;
};

export const getAlertBasedOnAddableToCartFailReasons = (
	addableToCartFailReasons: AddableToCartFailReasons[],
): { description: string; type: AlertType } | undefined => {
	if (addableToCartFailReasons.includes("CONFLICTING_HOURS")) {
		return {
			description: i18next.t("error_article_invalid_preferred_time_combination"),
			type: "error",
		};
	}
	return undefined;
};
